import {Box, Button, Paper, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {FC, ReactNode, useContext, useEffect, useState} from 'react'
import {ShowBase, useRecordContext, useTranslate} from 'react-admin'
import {useFormContext} from 'react-hook-form'

import TemplateEditorChatBackgroundImage
  from '../../assets/imgs/templateEditorChatBackground.png'
import ApiUrlsContext from '../../contexts/ApiUrlsContext'
import useIsDesktop from '../../hooks/useIsDesktop'
import Theme from '../../theme'
import FormattedChatMessageText from '../FormattedChatMessageText'
import {ArrowDownIcon, WhatsappBubbleTailIcon} from '../icons'
import LineClamp from '../LineClamp'
import ChatMessageTemplateButtonList from './ChatMessageTemplateButtonList'

const ChatMessageTemplateFormPreview: FC<ChatMessageTemplateFormPreviewProps> = ({
  source, ...props
}) => {
  const chatMessageTemplateId = useFormContext().watch(source)
  return (
    <ChatMessageTemplatePreview
      chatMessageTemplateId={chatMessageTemplateId}
      {...props}
    />
  )
}

const ChatMessageTemplatePreview: FC<ChatMessageTemplatePreviewProps> = ({
  chatMessageTemplateId, ...props
}) => chatMessageTemplateId ?
  (
    <ShowBase id={chatMessageTemplateId} resource="chat_message_templates">
      {/* @ts-ignore -- ignoring children missing */}
      <ChatMessageTemplateField {...props} />
    </ShowBase>
  ) : <ChatMessageTemplateField {...props} />

const ChatMessageTemplateField: FC<ChatMessageTemplateFieldProps> = ({
  className,
  isPropertiesPaneVisible = true,
  templateFormData,
  text,
}) => {
  const styles = useStyles()
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const templateRecord = useRecordContext() ?? templateFormData
  return (
    <div className={`${styles.root} ${className ?? ''}`}>
      <WhatsappSkin isSkeleton={!templateRecord}>
        {!!templateRecord?.headline && (
          <span className={styles.viewerHeadline}>{templateRecord.headline}</span>
        )}
        {(templateRecord?.headerMediaKey || templateRecord?.headerMediaUrl) && (
          <>
            {templateRecord.headerMediaMimeType?.startsWith('image/') && (
              <img
                alt="Template image"
                className={styles.templateImagePreview}
                loading="lazy"
                src={templateRecord?.headerMediaKey ?
                  getFilesApiUrl(templateRecord.headerMediaKey) :
                  templateRecord?.headerMediaUrl
                }
              />
            )}
            {templateRecord.headerMediaMimeType?.startsWith('video/') && (
              <video
                autoPlay
                className={styles.templateImagePreview}
                controls
                muted
                src={templateRecord?.headerMediaKey ?
                  getFilesApiUrl(templateRecord.headerMediaKey) :
                  templateRecord?.headerMediaUrl
                }
              />
            )}
            {templateRecord.headerMediaMimeType?.startsWith('application/') && (
              <iframe
                className={styles.templateImagePreview}
                loading="lazy"
                src={templateRecord?.headerMediaKey ?
                  getFilesApiUrl(templateRecord.headerMediaKey) :
                  templateRecord?.headerMediaUrl
                }
              />
            )}
          </>
        )}
        <FormattedChatMessageText
          className={styles.viewerText}
          text={text ?? templateRecord?.text as string}
        />
        {!!templateRecord?.footer &&
          <Typography
            variant="body2"
            color="textSecondary"
            className={styles.viewerFooter}
          >
            {templateRecord.footer}
          </Typography>
        }
        {!!templateRecord?.buttonText &&
          <div className={styles.button}>
            <span className={styles.viewerButtonText}>
              {templateRecord.buttonText}
            </span>
          </div>
        }
        <ChatMessageTemplateButtonList
          chatMessageTemplateButtons={templateRecord?.chatMessageTemplateButtons as any}
        />
      </WhatsappSkin>
      {isPropertiesPaneVisible && (
        <PreviewPaper
          buttons={templateRecord?.chatMessageTemplateButtons}
          // buttonURL here is kept for backward compatibility.
          buttonUrl={templateRecord?.buttonUrl}
          isChatMessageTemplateSelected={!!templateRecord}
        />
      )}
    </div>
  )
}

const WhatsappSkin: FC<WhatsappSkinProps> = ({children, isSkeleton}) => {
  const styles = useStyles()
  return (
    <div className={styles.whatsappSkin}>
      <Box position="relative" width="100%">
        <div className={styles.whatsappSkinBubble}>
          {isSkeleton ? (
            <Box className={styles.whatsappSkinBubbleSkeleton}>
              <div></div><div></div><div></div><div></div>
            </Box>
          ) : children}
        </div>
        <WhatsappBubbleTailIcon
          className={styles.whatsappBubbleTailIcon}
          color="white"
        />
      </Box>
    </div>
  )
}

const PreviewPaper = ({buttons, buttonUrl, isChatMessageTemplateSelected}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const isDesktop = useIsDesktop()
  const [isOpen, setIsOpen] = useState(isDesktop)
  useEffect(() => setIsOpen(isDesktop), [isDesktop])
  const hasUrlButtons = buttonUrl || buttons?.some(b => b.type === 'URL')
  return (
    <Paper className={styles.previewPaper}>
      {isChatMessageTemplateSelected && hasUrlButtons && (
        <>
          <Typography className={styles.previewPaperTitle} variant="h4">
            <Button
              disableRipple={isDesktop}
              onClick={() => !isDesktop && setIsOpen(o => !o)}
            >
              <Typography variant="subtitle1">
                {translate('chatMessageTemplateEditor.paperPreview.links')}
              </Typography>
              {!isDesktop &&
                <ArrowDownIcon
                  className={isOpen ? styles.previewPaperArrowDownIconIsOpen : ''}
                />
              }
            </Button>
          </Typography>
          {isOpen &&
            <div>
              <Typography variant="h6">
                {translate('chatMessageTemplateEditor.paperPreview.buttons')}
              </Typography>
              {buttonUrl && (
                <div className={styles.previewPaperDecorationValue}>{buttonUrl}</div>
              )}
              {buttons?.filter(b => b.type === 'URL')
                .map(({url, urlText}) => (
                  <Box marginTop="0.5rem">
                    <Typography color="textDisabled">{urlText}</Typography>
                    <div className={styles.previewPaperDecorationValue}>
                      <LineClamp>{url}</LineClamp>
                    </div>
                  </Box>
                ))}
            </div>
          }
        </>
      )}
    </Paper>
  )
}

// @ts-ignore
const useStyles = makeStyles<typeof Theme, Record<string, any>|undefined, any>(theme => {
  const buttonText = {
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    gap: theme.remSpacing(1),
    height: theme.typography.pxToRem(40),
    padding: 0,
    textAlign: 'center',
  }
  const headline = {
    display: 'block',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
    height: theme.typography.pxToRem(25),
    paddingLeft: 0,
    width: '100%',
  }
  const text = {
    color: theme.palette.text.secondary,
    display: 'block',
    fontWeight: 500,
  }
  return {
    addPlaceholderButton: {
      color: theme.palette.info.main,
      fontSize: 'smaller',
      fontWeight: 500,
      marginBottom: theme.remSpacing(1.5),
      marginLeft: 0,
      marginRight: 0,
      padding: theme.remSpacing(1),
      width: '100%',
    },
    button: {
      '& .MuiFormHelperText-root': {
        textAlign: 'center',
      },
      '& .MuiTextField-root': {
        '& .MuiFormHelperText-root': {
          margin: 0,
        },
        '& .MuiFormHelperText-root :not(Mui-error)': {
          display: 'none',
        },
        margin: 0,
        padding: 0,
        width: '60% !important',
      },
      alignItems: 'center',
      borderTop: `1px solid ${theme.palette.disabled.main}`,
      display: 'flex',
      fill: '#34B7F1',
      justifyContent: 'center',
      paddingBottom: theme.remSpacing(0.5),
      paddingTop: theme.remSpacing(0.5),
    },
    buttonInput: {
      '&$buttonValidLink': {
        color: theme.palette.info.main,
      },
      ...buttonText,
    },
    buttonValidLink: {},
    configurationsPanel: {
      '& .MuiFormControlLabel-root > *': {
        color: `${theme.palette.text.primary} !important`,
      },
      boxSizing: 'border-box',
      padding: theme.remSpacing(3),
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: '45%',
      },
    },
    divider: {
      marginBottom: theme.remSpacing(2),
      marginTop: theme.remSpacing(2),
    },
    headlineTextInput: {
      '& textarea': headline,
    },
    mediaFilePickerDefaultImage: {
      '& > :nth-child(3)': {
        marginBottom: theme.remSpacing(3),
      },
      '& > svg:first-of-type': {
        flexBasis: '100%',
        marginTop: theme.remSpacing(3),
        transform: 'scale(1.5)',
      },
      '& > svg:nth-of-type(2)': {
        marginBottom: theme.remSpacing(3),
        marginRight: theme.remSpacing(1),
        width: theme.typography.pxToRem(20),
      },
      alignItems: 'center',
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.typography.pxToRem(6),
      color: theme.palette.text.secondary,
      display: 'flex',
      flexWrap: 'wrap',
      fontWeight: '500',
      height: theme.typography.pxToRem(136),
      justifyContent: 'center',
      width: '100%',
    },
    previewPaper: {
      '& > div:not(hidden)': {
        marginBottom: theme.remSpacing(3),
        marginTop: theme.remSpacing(3),
      },
      boxSizing: 'border-box',
      padding: theme.remSpacing(3),
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: '40%',
      },
    },
    previewPaperArrowDownIconIsOpen: {
      rotate: '180deg',
    },
    previewPaperDecorationValue: {
      border: `1px solid ${theme.palette.disabled.main}`,
      borderRadius: theme.typography.pxToRem(2),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      marginTop: theme.remSpacing(1),
      padding: theme.remSpacing(1),
      textAlign: 'center',
    },
    previewPaperTitle: {
      '& button': {
        padding: 0,
      },
      '& button :first-child': {
        marginRight: theme.remSpacing(1),
      },
      '& svg': {
        transition: 'rotate 150ms',
        width: theme.typography.pxToRem(12),
      },
      borderBottom: `1px solid ${theme.palette.background.default}`,
      display: 'flex',
      justifyContent: 'center',
      paddingBottom: theme.remSpacing(.5),
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        flexDirection: 'row',
      },
    },
    templateImagePreview: {
      borderRadius: theme.typography.pxToRem(6),
      marginBottom: theme.typography.pxToRem(16),
      width: '100%',
    },
    textTextInput: {
      '& .MuiInput-root': {
        width: '100%',
      },
      '& textarea': {
        ...text,
      },
    },
    viewerButtonText: {
      ...buttonText,
      color: '#34B7F1',
      lineHeight: theme.typography.pxToRem(40),
      verticalAlign: 'middle',
    },
    viewerFooter: {
      marginBottom: theme.remSpacing(1.5),
    },
    viewerHeadline: {
      ...headline,
      marginBottom: theme.typography.pxToRem(16),
    },
    viewerText: {
      ...text,
      marginBottom: theme.typography.pxToRem(25),
      overflowWrap: 'anywhere',
      whiteSpace: 'break-spaces',
    },
    whatsappBubbleTailIcon: {
      backgroundColor: 'transparent',
      bottom: theme.typography.pxToRem(16),
      color: theme.palette.background.paper,
      left: theme.typography.pxToRem(-16),
      position: 'absolute',
    },
    whatsappSkin: {
      alignItems: 'center',
      backgroundImage: `url(${TemplateEditorChatBackgroundImage})`,
      display: 'flex',
      fill: theme.palette.text.secondary,
      [theme.breakpoints.up('lg')]: {
        width: '100%',
      },
      margin: 0,
      paddingBottom: theme.remSpacing(5),
      paddingLeft: theme.remSpacing(4),
      paddingRight: theme.remSpacing(4.5),
      paddingTop: theme.remSpacing(2.5),
    },
    whatsappSkinBubble: {
      '& .MuiInputBase-input': {
        border: 0,
      },
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.typography.pxToRem(10),
      maxWidth: theme.typography.pxToRem(350),
      padding: theme.remSpacing(2),
      width: '80%',
      [theme.breakpoints.down('xl')]: {
        width: '95%',
      },
    },
    whatsappSkinBubbleSkeleton: {
      '& > *': {
        backgroundColor: theme.palette.background.default,
        height: theme.typography.pxToRem(14),
        marginBottom: theme.remSpacing(1),
      },
      '& > :nth-child(1)': {
        width: '60%',
      },
      '& > :nth-child(2)': {
        marginBottom: theme.remSpacing(5),
      },
      '& > :nth-child(4)': {
        width: '90%',
      },
      padding: theme.remSpacing(3),
      paddingBottom: theme.remSpacing(8),
    },
  }
})

interface ChatMessageTemplateFormPreviewProps {
  [p: string]: any
  source: string
}

interface ChatMessageTemplatePreviewProps extends ChatMessageTemplateFieldProps {
  [p: string]: any
  chatMessageTemplateId?: string
}

interface ChatMessageTemplateFieldProps {
  className?: string
  isPropertiesPaneVisible?: boolean
  templateFormData?: Record<string, any>
  text?: string
}

interface WhatsappSkinProps {
  children: ReactNode
  isSkeleton?: boolean
}

export {
  ChatMessageTemplateField,
  ChatMessageTemplateFormPreview,
  ChatMessageTemplatePreview,
}
